import React, { useState, useMemo } from "react";
import {
  Box,
  Chip,
  FormControl,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Select,
  TextField,
  InputAdornment,
} from "@mui/material";
import { Search, X } from "lucide-react";

const MultiSelect = ({
  value = [],
  handleChange,
  title,
  name,
  items = [],
  canSearch = false,
}) => {
  const [searchText, setSearchText] = useState("");
  const selectedValues = Array.isArray(value) ? value : [];

  const filteredItems = useMemo(() => {
    if (!canSearch || !searchText.trim()) return items;
    return items.filter((item) =>
      item.label?.toLowerCase().includes(searchText.toLowerCase()),
    );
  }, [items, searchText, canSearch]);

  const handleRemove = (removedValue) => {
    const updated = selectedValues.filter((v) => v !== removedValue);
    handleChange(name, updated);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <FormControl sx={{ width: "100%" }} size="small">
        {title && <InputLabel id={`${name}-label`}>{title}</InputLabel>}
        <Select
          labelId={title ? `${name}-label` : undefined}
          multiple
          value={selectedValues.filter((v) => v !== null && v !== undefined)}
          onChange={(event) => {
            const {
              target: { value: val },
            } = event;
            const arr =
              typeof val === "string"
                ? val.split(",")
                : Array.isArray(val)
                  ? val
                  : [];
            const cleaned = arr.filter(
              (v) => v !== null && v !== undefined && v !== "",
            );
            handleChange(name, cleaned);
          }}
          input={
            <OutlinedInput id={`${name}-chip`} label={title || undefined} />
          }
          // Hide selected chips inside the box — shown below instead
          renderValue={(selected) => (
            <Box sx={{ color: "#6b7280", fontSize: 13 }}>
              {selected.length === 0
                ? "Select..."
                : `${selected.length} selected`}
            </Box>
          )}
          MenuProps={{
            autoFocus: false,
            PaperProps: {
              style: { maxHeight: 300 },
            },
          }}
          // Stop search input from closing the dropdown on keydown
          onClose={() => setSearchText("")}
        >
          {/* Search input inside dropdown */}
          {canSearch && (
            <Box
              sx={{
                px: 1,
                pb: 1,
                pt: 0.5,
                position: "sticky",
                top: 0,
                background: "#fff",
                zIndex: 1,
              }}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <TextField
                size="small"
                fullWidth
                placeholder="Search..."
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (e.key === "Enter") {
                    e.preventDefault();
                  }
                }}
                onClick={(e) => e.stopPropagation()}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={14} color="#9ca3af" />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    fontSize: 13,
                  },
                }}
              />
            </Box>
          )}

          {filteredItems.length === 0 ? (
            <MenuItem disabled sx={{ fontSize: 13, color: "#9ca3af" }}>
              No options found
            </MenuItem>
          ) : (
            filteredItems.map((item) => (
              <MenuItem
                key={item.value}
                value={item.value}
                sx={{
                  fontSize: 13,
                  backgroundColor: selectedValues.includes(item.value)
                    ? "#f0f4ff !important"
                    : "transparent",
                  fontWeight: selectedValues.includes(item.value) ? 600 : 400,
                }}
              >
                {item.label}
              </MenuItem>
            ))
          )}
        </Select>
      </FormControl>

      {/* Selected items shown BELOW the box */}
      {selectedValues.length > 0 && (
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 0.7,
            mt: 1,
          }}
        >
          {selectedValues.map((val) => {
            const label = items.find((i) => i.value === val)?.label;
            if (!label) return null;
            return (
              <Box
                key={val}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 0.5,
                  backgroundColor: "#f3f4f6",
                  borderRadius: "6px",
                  px: 1,
                  py: 0.4,
                  fontSize: 12,
                  fontWeight: 500,
                  color: "#374151",
                  maxWidth: "100%",
                }}
              >
                <span
                  style={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: 160,
                  }}
                >
                  {label}
                </span>
                <Box
                  component="span"
                  onClick={() => handleRemove(val)}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    cursor: "pointer",
                    color: "#6b7280",
                    ml: 0.3,
                    flexShrink: 0,
                    "&:hover": { color: "#111827" },
                  }}
                >
                  <X size={12} strokeWidth={2.5} />
                </Box>
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default MultiSelect;
